import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../utils";
import { Group } from "./group";
import { Icon, type MaterialSymbol } from "./icon";
import { Spinner } from "./loading";

const alertVariants = cva(
  "relative w-full flex gap-2.5 rounded-lg border p-4 pt-3.5 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4",
  {
    variants: {
      variant: {
        default: "border-foreground/20 bg-foreground/5 text-foreground/80 [&>svg]:text-foreground/80",
        primary: "border-primary/20 bg-primary/2 text-primary [&>svg]:text-primary",
        destructive: "border-destructive/20 bg-destructive/10 text-destructive [&>svg]:text-destructive",
        success: "border-success/20 bg-success/10 text-success [&>svg]:text-success",
        warning: "border-warning/20 bg-warning/10 text-warning [&>svg]:text-warning",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export type AlertProps = React.ComponentPropsWithRef<"div"> &
  VariantProps<typeof alertVariants> & { icon?: MaterialSymbol; loading?: boolean };

function Alert({ children, className, variant, icon, loading, ...props }: AlertProps) {
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {(icon || loading) && (
        <div className="flex flex-none items-center justify-center size-5 text-xl">
          {loading ? <Spinner className="text-lg" /> : icon && <Icon icon={icon} />}
        </div>
      )}
      <Group className="gap-1 flex-1">{children}</Group>
    </div>
  );
}

function AlertTitle({ asChild, className, ...props }: React.ComponentPropsWithRef<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "h4";
  return <Comp className={cn("text-base font-semibold", className)} {...props} />;
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col gap-2 text-sm", className)} {...props} />;
}

export { Alert, AlertDescription, AlertTitle };
